import {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { z } from 'zod';
import { useAutoAnimateConditional } from '@/hooks/useAutoAnimateConditional';
import { useForm, SubmitHandler, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCustomErrorMap } from '@/hooks/useCustomErrorMap';
import { FormattedMessage } from 'react-intl';
import { InputText } from '../Inputs/InputText';
import { InputPostalCode } from '../Inputs/InputPostalCode';
import { IconCheck, IconPlus } from '@/icons';
import Button from '../core/Button';
import {
  useGetPayerForDepartment,
  useCreatePayer,
  getGetPayerForDepartmentQueryKey,
  useUpdatePayerForDepartment,
} from '@/api/trafikportalen';
import { EditButton } from '../EditButton';
import { createPayerBody } from '@/api/trafikportalen/trafikportalenAPI.zod';
import { Loading } from '../Loading';
import { DataNotFound } from '../DataNotFound';
import { toast } from '@/hooks/useToast';
import { useQueryClient } from '@tanstack/react-query';
import { trimData } from '@/helpers/trimData';
import { hasDepartmentWriteAccess } from '@/helpers/departmentPermissions';

const payerFormSchema = createPayerBody;

type PayerType = z.infer<typeof payerFormSchema>;

interface DepartmentDetailsProps {
  departmentId: string;
}

export const DepartmentPayerForm: FC<DepartmentDetailsProps> = (props) => {
  const [ref] = useAutoAnimateConditional<HTMLFormElement>();

  const customErrorMap = useCustomErrorMap();
  const queryClient = useQueryClient();

  const [isEditMode, setIsEditMode] = useState(false);

  const {
    data: payer,
    isLoading,
    isError,
  } = useGetPayerForDepartment(props.departmentId);

  const createPayer = useCreatePayer();
  const updatePayer = useUpdatePayerForDepartment();

  const { handleSubmit, control, formState, reset } = useForm<PayerType>({
    resolver: zodResolver(payerFormSchema, {
      errorMap: customErrorMap,
    }),
    mode: 'all',
  });

  const watchedValues = useWatch({ control });

  const defaultValues = useMemo(() => {
    if (!payer) return;

    return {
      address: payer.address,
      details: payer.details,
      reference: payer.reference,
      department_id: props.departmentId,
    };
  }, [payer, props.departmentId]);

  const payerMatchesInherited = useMemo(() => {
    if (!payer || payer.is_direct_payer || !isEditMode) return false;

    return (
      watchedValues?.address?.street === payer.address.street &&
      watchedValues?.address?.zip_code === payer.address.zip_code &&
      watchedValues?.address?.city === payer.address.city &&
      watchedValues?.reference === payer.reference
    );
  }, [payer, watchedValues, isEditMode]);

  const allRequiredFieldsFilled = useMemo(() => {
    return (
      !!watchedValues?.address?.street &&
      !!watchedValues?.address?.zip_code &&
      !!watchedValues?.address?.city &&
      !!watchedValues?.reference
    );
  }, [watchedValues]);

  const handleSuccess = useCallback(async () => {
    toast.success(
      { title: 'Ny faktureringsadress sparad' },
      { autoClose: 3000 },
    );
    await queryClient.invalidateQueries({
      queryKey: getGetPayerForDepartmentQueryKey(props.departmentId),
    });
    setIsEditMode(false);
  }, [queryClient, props.departmentId]);

  const handleError = useCallback((error: Error) => {
    console.error('Error: ', error);
    toast.error(
      {
        title: 'Ett fel inträffade',
        description:
          'Försök spara igen senare, eller vänligen kontakta support.',
      },
      { autoClose: 3000 },
    );
  }, []);

  const onSubmit: SubmitHandler<PayerType> = (data) => {
    if (!props.departmentId) {
      console.error('Department ID is missing');
    }

    if (payer?.is_direct_payer) {
      updatePayer.mutate(
        {
          departmentID: props.departmentId,
          data: trimData(data),
        },
        {
          onSuccess: handleSuccess,
          onError: handleError,
        },
      );
    } else {
      createPayer.mutate(
        {
          data: trimData({
            ...data,
            department_id: props.departmentId,
          }),
        },
        {
          onSuccess: handleSuccess,
          onError: handleError,
        },
      );
    }
  };

  const handleCreatePayer = useCallback(() => {
    setIsEditMode(true);
  }, []);

  useEffect(() => {
    // If payer is available, always set the default values
    if (!payer) return;

    reset(defaultValues);

    setIsEditMode(false);
  }, [payer, reset, defaultValues]);

  const renderEditMode = useMemo(() => {
    if (!payer?.is_direct_payer) {
      return (
        <Button
          startIcon={<IconPlus />}
          onClick={handleCreatePayer}
          variant="outline"
        >
          <FormattedMessage defaultMessage="Ange nya faktureringsuppgifter" />
        </Button>
      );
    }
    return <EditButton onClickButton={() => setIsEditMode(!isEditMode)} />;
  }, [payer, handleCreatePayer, isEditMode]);

  if (isLoading) {
    return <Loading />;
  }

  if (isError) {
    return (
      <DataNotFound
        description={
          <FormattedMessage defaultMessage="Faktureringsadress ej tillgänglig" />
        }
      />
    );
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="flex flex-col py-4 @container md:pt-8"
      ref={ref}
    >
      <div className="flex w-full flex-row flex-wrap justify-between gap-4">
        <h2>
          <FormattedMessage defaultMessage="Faktureringsuppgifter" />
        </h2>
        {!isEditMode && hasDepartmentWriteAccess(props.departmentId)
          ? renderEditMode
          : null}
      </div>
      <p className="my-4 text-text-700">
        {!payer?.is_direct_payer && (
          <>
            <FormattedMessage defaultMessage="Faktureringsuppgifterna som används är hämtade från: " />{' '}
            {payer?.department_name}
          </>
        )}
      </p>

      <div className="mb-8 grid grid-cols-1 flex-wrap gap-4 @xl:grid-cols-3 @2xl:grid-cols-4">
        <DeliveryFormElement
          label={<FormattedMessage defaultMessage="Adress" />}
          input={
            <InputText
              name="address.street"
              control={control}
              editMode={isEditMode}
              error={formState.errors.address?.street}
            />
          }
        />
        <DeliveryFormElement
          label={<FormattedMessage defaultMessage="Postnummer" />}
          input={
            <InputPostalCode
              name="address.zip_code"
              control={control}
              editMode={isEditMode}
              error={formState.errors.address?.zip_code}
            />
          }
        />
        <DeliveryFormElement
          label={<FormattedMessage defaultMessage="Postort" />}
          input={
            <InputText
              name="address.city"
              control={control}
              editMode={isEditMode}
              error={formState.errors.address?.city}
            />
          }
        />
        <DeliveryFormElement
          label={<FormattedMessage defaultMessage="Referens" />}
          input={
            <InputText
              name="reference"
              control={control}
              editMode={isEditMode}
              error={formState.errors.reference}
            />
          }
        />
      </div>
      {isEditMode && (
        <>
          {!payer?.is_direct_payer && payerMatchesInherited && (
            <div className="mb-4 rounded bg-yellow-200 p-3 text-contrast-yellow">
              <FormattedMessage defaultMessage="De angivna faktureringsuppgifterna är identiska med de uppgifter som redan nyttjats från ovanstående avdelning. Ändra minst ett fält för att kunna spara." />
            </div>
          )}
          <span className="text-text-700">
            <FormattedMessage defaultMessage="Genom att spara faktureringsuppgifter för denna avdelning, kommer dessa uppgifter att användas för all fakturering för denna och underliggande avdelningar." />
          </span>
          <div className="mt-4 flex justify-end gap-2">
            <Button
              variant="ghost"
              onClick={() => {
                setIsEditMode(!isEditMode);
                reset(defaultValues);
              }}
            >
              <FormattedMessage defaultMessage="Avbryt" />
            </Button>
            <Button
              type="submit"
              variant="filled"
              startIcon={<IconCheck />}
              disabled={
                payer?.is_direct_payer
                  ? !allRequiredFieldsFilled
                  : !allRequiredFieldsFilled || payerMatchesInherited
              }
            >
              {payer?.is_direct_payer ? (
                <FormattedMessage defaultMessage="Uppdatera faktureringsadress" />
              ) : (
                <FormattedMessage defaultMessage="Spara ny faktureringsadress" />
              )}
            </Button>
          </div>
        </>
      )}
    </form>
  );
};

interface DeliveryItemProps {
  label: ReactNode;
  input?: ReactNode;
}

const DeliveryFormElement = ({ label, input }: DeliveryItemProps) => {
  return (
    <div className="flex w-full min-w-[150px] flex-col sm:w-auto sm:flex-1">
      <span className="font-medium text-text-900">{label}</span>
      {input}
    </div>
  );
};
